import { describeApiError, type PointsSystem } from '@brush/api-client';
import {
  ErrorNotice,
  Spinner,
  blankDefinition,
  inputClass,
  readDefinition,
  shrpoDefinition,
  toDefinitionDocument,
} from '@brush/ui';
import { useState } from 'react';
import { useCreatePlatformPointsSystem, usePointsSystemVersion } from '../../queries';
import { Dialog } from './Dialog';

/**
 * What the dialog was opened to do.
 *
 * Duplicating is a **create that starts from an existing definition**, not a third kind of save.
 * The server's own clone route always lands a copy in a *room* — that is what it is for, §2.2's
 * "an operator may clone a platform system" — so it has nothing to offer a platform administrator
 * copying one platform system into another. Reading the source's definition and creating a new
 * system with it is the same thing the template catalogue already does for the same reason.
 */
export type NewSystemIntent =
  | { mode: 'create'; start: 'blank' | 'shrpo' }
  | { mode: 'duplicate'; source: PointsSystem; versionNumber: number };

export function NewPointsSystemDialog({
  intent,
  onClose,
  onCreated,
}: {
  intent: NewSystemIntent;
  onClose: () => void;
  /** Hands back the new system so the area can open the builder on it straight away. */
  onCreated: (system: PointsSystem) => void;
}) {
  const duplicating = intent.mode === 'duplicate';

  // Only fetched when duplicating: a system row carries version summaries, never the definitions.
  const source = usePointsSystemVersion(
    duplicating ? intent.source.id : null,
    duplicating ? intent.versionNumber : null,
  );

  const [name, setName] = useState(() =>
    duplicating ? `${intent.source.name} (copy)` : intent.mode === 'create' && intent.start === 'shrpo' ? 'SHRPO-style tournament leaderboard' : '',
  );
  const create = useCreatePlatformPointsSystem();

  const waiting = duplicating && (source.isLoading || source.isError);

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;

    // The definition carries its own name and so does the catalogue row. They are set together
    // here and kept together by the builder, so the list and the document never disagree about
    // what the system is called.
    const definition = duplicating
      ? { ...readDefinition(source.data?.definition), name: trimmed }
      : intent.start === 'shrpo'
        ? shrpoDefinition(trimmed)
        : blankDefinition(trimmed);

    create.mutate(
      {
        name: trimmed,
        description: definition.description,
        definition: toDefinitionDocument(definition),
      },
      { onSuccess: onCreated },
    );
  };

  return (
    <Dialog
      title={duplicating ? 'Duplicate points system' : 'New points system'}
      subtitle={
        duplicating
          ? `A new platform system, starting from version ${intent.versionNumber} of ${intent.source.name}.`
          : 'Owned by the platform. Every room can use it as it stands, or clone it and make it their own.'
      }
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-ink-800/15 py-2 text-sm text-ink-800 hover:bg-ink-800/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={name.trim().length === 0 || create.isPending || waiting}
            className="flex-1 rounded-lg bg-felt-700 py-2 text-sm font-medium text-white hover:bg-felt-900 disabled:opacity-50"
          >
            {create.isPending ? 'Creating…' : duplicating ? 'Create copy' : 'Create and open'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-xs text-ink-800/60">Name</span>
          <input
            type="text"
            value={name}
            autoFocus
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
            placeholder="Summer Series Player of the Year"
            className={inputClass}
          />
        </label>

        {duplicating ? (
          source.isLoading ? (
            <Spinner label="Reading the definition to copy…" />
          ) : source.isError ? (
            <ErrorNotice message="Couldn’t read that version’s definition, so there is nothing to copy yet." />
          ) : (
            <p className="text-xs text-ink-800/60">
              Everything from that version comes across — the scope, the placement table, the
              weightings. The copy starts as its own draft and is independent from the moment it
              exists.
            </p>
          )
        ) : (
          <p className="text-xs leading-relaxed text-ink-800/60">
            {intent.mode === 'create' && intent.start === 'shrpo'
              ? 'Starts from the published SHRPO ladder: 100 for a win down to a flat 10 from 28th, weighted by field size, a point for every entry that didn’t cash. Every figure is editable.'
              : 'Starts from a plain ladder covering the next three months, with no weighting and no points for busting out. You will set the dates and the table on the next screen.'}
          </p>
        )}

        <ErrorNotice message={describeApiError(create.error)} />
      </div>
    </Dialog>
  );
}
